import { ConditionType, StepType } from '@de/types/enums';
import type { Step } from '@de/types/types';

export const ACTION_OUTCOME_QUESTION = 'dexp-question-flow';
export const ACTION_OUTCOME_RESULTS = 'dexp-results';

export type ActionOutcome =
  | typeof ACTION_OUTCOME_QUESTION
  | typeof ACTION_OUTCOME_RESULTS;

const isResultStep = (step: Step | undefined): boolean =>
  step?.type === StepType.RESULT;

/**
 * Resolve the *default* `actionOutcome` for the eventContext that backs
 * entry-point Product/Form events.
 *
 * Per §6.4 of the Dynamic Experience Eventing Requirements:
 *
 *   actionOutcome = nextStep?.type === RESULT ? 'dexp-results' : 'dexp-question-flow'
 *
 * `getNextStep` is data-driven (depends on the user's selection) and async, so
 * this resolver evaluates it _structurally_ from the current step + steps
 * array — it can do so synchronously because the `actionOutcome` is set on
 * `eventContext` before any selection is made. We classify the default outcome
 * as `dexp-results` when **every** reachable next step from `currentStep` is a
 * `RESULT` step, or when the current step is terminal (next is the result
 * page). Any path leading to another question keeps `dexp-question-flow`.
 *
 * For per-CTA differentiation when a single render emits multiple
 * `ProductClicked` events (one per CTA), use `resolveActionOutcomeForStep` /
 * `resolveActionOutcomeForBranch` and override `actionOutcome` on the click's
 * event-context payload. Those helpers look at the specific branch the CTA
 * leads to, so two CTAs on the same step can resolve to different outcomes.
 */
export const resolveActionOutcome = (
  currentStep: Step | undefined,
  steps: Step[] = [],
): ActionOutcome => {
  if (!currentStep) return ACTION_OUTCOME_QUESTION;

  const branches = currentStep.branchingLogic ?? [];
  if (branches.length === 0) {
    return currentStep.isTerminal
      ? ACTION_OUTCOME_RESULTS
      : ACTION_OUTCOME_QUESTION;
  }

  const stepById = new Map(steps.map((s) => [s.id, s]));
  const targets = branches
    .filter((b) => b?.condition?.type !== undefined)
    .map((b) => stepById.get((b as { nextId?: string }).nextId ?? ''))
    .filter((s): s is Step => Boolean(s));

  if (targets.length === 0) {
    return currentStep.isTerminal
      ? ACTION_OUTCOME_RESULTS
      : ACTION_OUTCOME_QUESTION;
  }

  // Every reachable next step must be a RESULT for the outcome to be results.
  // If any branch leads back into the flow, treat as a question continuation.
  const everyResult = targets.every(isResultStep);
  return everyResult ? ACTION_OUTCOME_RESULTS : ACTION_OUTCOME_QUESTION;
};

/**
 * Resolve the `actionOutcome` for a *specific* CTA whose target next step is
 * already known.
 *
 * This is the per-CTA counterpart to `resolveActionOutcome`. Use it at click
 * time once you've determined where this particular CTA navigates the user
 * (e.g. via `getNextStep` or by reading `branchingLogic[i].nextId`). It
 * returns `dexp-results` when the target is a RESULT step or when there is
 * no further step (the click terminates the flow → results), and
 * `dexp-question-flow` otherwise.
 */
export const resolveActionOutcomeForStep = (
  nextStep: Step | undefined,
): ActionOutcome =>
  isResultStep(nextStep) || nextStep === undefined
    ? ACTION_OUTCOME_RESULTS
    : ACTION_OUTCOME_QUESTION;

/**
 * Resolve the `actionOutcome` for a CTA on `currentStep` whose branch target
 * is `nextId`. Convenience wrapper around `resolveActionOutcomeForStep` for
 * call sites that have a `nextId` rather than a fully-resolved `Step`.
 *
 * If the branch's `nextId` doesn't resolve to a known step, falls back to the
 * default whole-step resolver so we never silently emit a wrong value.
 */
export const resolveActionOutcomeForBranch = (
  currentStep: Step | undefined,
  steps: Step[] = [],
  nextId: string | undefined,
): ActionOutcome => {
  if (!nextId) return resolveActionOutcome(currentStep, steps);
  const target = steps.find((s) => s.id === nextId);
  if (!target) return resolveActionOutcome(currentStep, steps);
  return resolveActionOutcomeForStep(target);
};

// `ConditionType` is exported for test ergonomics (callers do not depend on it).
export { ConditionType };
